import axios from "axios";
import { history } from "../history";
import { fireGtmEvent } from "../utils/googleAnalytics";
import storageService from "./storageService";
import { captureException } from "@sentry/react";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Request interceptor to add corporate token and agentId if available
axiosInstance.interceptors.request.use((request) => {
  // Check for corporate token first
  const corporateToken = localStorage.getItem("corporateToken");
  const corporateHotelId = localStorage.getItem("corporateHotelId");
  const corporateAgentId = localStorage.getItem("corporateAgentId");

  console.log({ corporateAgentId, corporateHotelId, corporateToken });

  if (corporateToken && corporateHotelId) {
    // Use corporate token for authorization
    request.headers.Authorization = `Bearer ${corporateToken}`;

    // Add agentId to params if it's a GET request
    console.log({ request });
    if (request.method === "get" && corporateAgentId) {
      request.params = {
        ...request.params,
        corporateAgentId: corporateAgentId,
      };
    }

    // For POST/PUT requests, add to body
    if (
      ["post", "put", "patch"].includes(request.method) &&
      corporateAgentId &&
      request.data
    ) {
      request.data = {
        ...request.data,
        corporateAgentId: corporateAgentId,
      };
    }
  } else {
    // Fall back to regular token
    const token = storageService.get("token");
    if (token) {
      request.headers.Authorization = `Bearer monne ${token}`;
    }
  }

  return request;
});

// axiosInstance.interceptors.request.use((request) => {
//   const token = storageService.get("corporateToken");
//   request.headers.Authorization = `Bearer ${token}`;
//   return request;
// });

// Response interceptor to handle auth errors
axiosInstance.interceptors.response.use(null, (error) => {
  const { status } = error?.response || {};

  if (status === 401) {
    // Clear corporate tokens if they exist
    localStorage.removeItem("corporateToken");
    localStorage.removeItem("corporateHotelId");
    localStorage.removeItem("corporateAgentId");

    // Clear regular token
    storageService.remove("token");

    // Check if we were on a corporate booking flow
    const urlParams = new URLSearchParams(window.location.search);
    const hotelId = urlParams.get("hotelId");
    const isCorporate = urlParams.get("corporate");

    if (isCorporate && hotelId) {
      // Redirect to corporate login
      window.location.href = `/corporate-booking?hotelId=${hotelId}&returnUrl=${encodeURIComponent(
        window.location.pathname + window.location.search
      )}`;
    } else {
      // Regular redirect
      history.push("/");
    }
  }

  return Promise.reject(error);
});

const tryCatchWrapper = async (axiosObject) => {
  try {
    const { data } = await axiosInstance(axiosObject);

    if (data?.status === 400) {
      fireGtmEvent("api_error", {
        url: axiosObject?.url,
        params: axiosObject?.params,
        payload: axiosObject?.data,
        data,
      });
    }
    return data;
  } catch (error) {
    if (error?.response?.status >= 500) {
      captureException(`API: ${axiosObject?.url?.slice(0, 30)} Failed!`, {
        data: { request: axiosObject, error },
      });
    }
    fireGtmEvent("api_error", {
      url: axiosObject?.url,
      axiosObject,
      error,
    });

    return error?.response?.data || {};
  }
};

const corporateHttpService = {
  get: (url, params) => tryCatchWrapper({ method: "GET", url, params }),
  post: (url, body) => tryCatchWrapper({ method: "POST", url, data: body }),
  put: (url, body) => tryCatchWrapper({ method: "PUT", url, data: body }),
  patch: (url, body) => tryCatchWrapper({ method: "PATCH", url, data: body }),
  delete: (url, body) => tryCatchWrapper({ method: "DELETE", url, data: body }),

  // Helper method to check if using corporate booking
  isCorporateBooking: () => {
    return (
      !!localStorage.getItem("corporateToken") &&
      !!localStorage.getItem("corporateHotelId")
    );
  },

  // Helper method to get availability endpoint
  // Uses the same v2/availability endpoint for both regular and corporate bookings
  getAvailabilityEndpoint: (isRateplan = false) => {
    const isCorporate = corporateHttpService.isCorporateBooking();
    // For corporate bookings, use authorized endpoint which will apply corporate filtering
    // For regular bookings, use unauthorized endpoints
    if (isCorporate) {
      // Always use the main availability endpoint for corporate bookings
      return "hotel/authorized/v2/hotel/availability";
    } else {
      return isRateplan
        ? "hotel/v2/hotel/availability/rateplan"
        : "hotel/v2/hotel/availability";
    }
  },

  // Clear corporate session
  clearCorporateSession: () => {
    localStorage.clear()
  },
};

export default corporateHttpService;
